import { useCallback, useEffect, useRef, useState } from 'react'
import type { CategoryGroup } from './groupCategories'
import { groupCategories } from './groupCategories'
import type { CatalogCategory } from './types'
import { defaultCatalogSearchService } from './useCatalogSearch'

/**
 * The narrow seam this hook actually needs — `CatalogSearchService` (from
 * `useCatalogSearch.ts`) already implements it, so the real BFF-backed
 * default just reuses that service rather than standing up a second HTTP
 * client for the same `GET /catalog/categories` endpoint.
 */
export interface CategoryListingService {
  listCategories(): Promise<readonly CatalogCategory[]>
}

export type CategoryTaxonomyState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | {
      readonly status: 'ready'
      readonly groups: readonly CategoryGroup[]
      readonly other: readonly string[]
      /** The raw enriched categories (name + imageUrl + seriesCount), keyed
       * by nothing in particular here — consumers that need per-category
       * image/count (e.g. `ShopByCategory`) build their own `Map` from this. */
      readonly categories: readonly CatalogCategory[]
    }

export type UseCategoryTaxonomyResult = CategoryTaxonomyState & {
  /** Re-runs `listCategories()` — the "Try again" action for the error
   * state; a fresh fetch, not a client-side re-group of stale data. */
  readonly retry: () => void
}

/**
 * Data-layer seam for the "Shop by category" browse landing (UID-253
 * rebuild): fetches the real category list once, then groups it under the
 * static `CATALOG_TAXONOMY_GROUPS` taxonomy (`groupCategories.ts`). Fetches
 * on mount and on `retry()` only — there is no keyword/filter state here to
 * react to, unlike `useCatalogSearch`.
 */
export function useCategoryTaxonomy(
  service: CategoryListingService = defaultCatalogSearchService,
): UseCategoryTaxonomyResult {
  const [state, setState] = useState<CategoryTaxonomyState>({ status: 'loading' })
  const serviceRef = useRef(service)
  serviceRef.current = service

  const load = useCallback(() => {
    setState({ status: 'loading' })
    let cancelled = false
    serviceRef.current
      .listCategories()
      .then((categories) => {
        if (cancelled) return
        const { groups, other } = groupCategories(categories.map((category) => category.name))
        setState({ status: 'ready', groups, other, categories })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const message =
          error instanceof Error ? error.message : 'We couldn’t load the catalog categories.'
        setState({ status: 'error', message })
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => load(), [load])

  return { ...state, retry: load }
}
